import { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_BASE } from '../../../../ApiUrl';
import {
  createEmptyLanguage,
  createEmptyStandard,
  createEmptyEquipment,
  createEmptySoftware,
} from '../constants';

const CAPABILITY_URL = `${API_BASE}/capability-records/`;

export const useCapabilityRecords = (professionalProfileId) => {
  const [languageRecords, setLanguageRecords] = useState([createEmptyLanguage()]);
  const [standardRecords, setStandardRecords] = useState([createEmptyStandard()]);
  const [equipmentRecords, setEquipmentRecords] = useState([createEmptyEquipment()]);
  const [softwareRecords, setSoftwareRecords] = useState([createEmptySoftware()]);

  // ----- Hydration -----
  const PROFICIENCY_MAP_REVERSE = {
    AWARE: 'Aware',
    SUPERVISED: 'Supervised',
    INDEPENDENT: 'Independent',
    EXPERT: 'Expert',
  };

  const hydrateFromCapabilities = (records) => {
    if (!Array.isArray(records) || records.length === 0) return;

    const byType = {};
    records.forEach((rec) => {
      if (!byType[rec.capability_type]) byType[rec.capability_type] = [];
      byType[rec.capability_type].push(rec);
    });

    // Languages
    const langRecords = byType.LANGUAGE || [];
    if (langRecords.length > 0) {
      setLanguageRecords(
        langRecords.map((rec) => ({
          id: `lang-${rec.id}`,
          language: rec.custom_name || '',
          speaking: rec.speaking_level || '',
          reading: rec.reading_level || '',
          writing: rec.writing_level || '',
          errors: {},
        }))
      );
    }

    // Standards
    const stdRecords = byType.STANDARD || [];
    if (stdRecords.length > 0) {
      setStandardRecords(
        stdRecords.map((rec) => ({
          id: `standard-${rec.id}`,
          standard: rec.custom_name || '',
          edition: rec.edition_or_model || '',
          proficiency: PROFICIENCY_MAP_REVERSE[rec.proficiency] || '',
          last_used: rec.last_used_date || '',
          errors: {},
        }))
      );
    }

    // Equipment
    const eqRecords = byType.EQUIPMENT || [];
    if (eqRecords.length > 0) {
      setEquipmentRecords(
        eqRecords.map((rec) => ({
          id: `equipment-${rec.id}`,
          instrument_name: rec.custom_name || '',
          make_model: rec.edition_or_model || '',
          proficiency: PROFICIENCY_MAP_REVERSE[rec.proficiency] || '',
          last_used: rec.last_used_date || '',
          calibration_authority: rec.details?.calibration_authority || '',
          errors: {},
        }))
      );
    }

    // Software
    const swRecords = byType.SOFTWARE || [];
    if (swRecords.length > 0) {
      setSoftwareRecords(
        swRecords.map((rec) => ({
          id: `software-${rec.id}`,
          software_name: rec.custom_name || '',
          proficiency: PROFICIENCY_MAP_REVERSE[rec.proficiency] || '',
          years_of_use:
            rec.years_experience === null || rec.years_experience === undefined
              ? ''
              : String(rec.years_experience),
          last_used: rec.last_used_date || '',
          errors: {},
        }))
      );
    }
  };

  // Hydrate from related records - Handle capabilities from unified API
  const hydrateFromRelatedRecords = (data) => {
    if (!data) return;
    
    // The API returns capabilities directly in the data object
    if (data.capabilities && Array.isArray(data.capabilities)) {
      hydrateFromCapabilities(data.capabilities);
    }
    
    // Also check for nested capabilities in case the API structure changes
    if (data.capability_records && Array.isArray(data.capability_records)) {
      hydrateFromCapabilities(data.capability_records);
    }
  };

  // Fetch from related-records endpoint only
  useEffect(() => {
    if (!professionalProfileId) return;

    const loadCapabilities = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/professional-profiles/${professionalProfileId}/related-records/`
        );
        const data = res?.data?.data || res?.data;
        
        // Hydrate capabilities data
        hydrateFromRelatedRecords(data);
      } catch (err) {
        console.error('Failed to load capabilities from related-records API', err);
        // Keep the default empty states
      }
    };

    loadCapabilities();
  }, [professionalProfileId]);

  // ----- Extract backend ID -----
  const extractBackendId = (recordId) => {
    const match = /^(?:lang|standard|equipment|software)-(\d+)$/.exec(String(recordId));
    return match ? match[1] : null;
  };

  // ----- Build payload -----
  const PROFICIENCY_MAP = {
    Aware: 'AWARE',
    Supervised: 'SUPERVISED',
    Independent: 'INDEPENDENT',
    Expert: 'EXPERT',
  };

  const buildPayload = () => {
    const records = [];

    // Languages
    languageRecords
      .filter((rec) => rec.language)
      .forEach((rec) => {
        records.push({
          _backendId: extractBackendId(rec.id),
          capability_type: 'LANGUAGE',
          reference_value: null,
          custom_name: rec.language || '',
          related_scope: null,
          edition_or_model: '',
          proficiency: '',
          speaking_level: rec.speaking || '',
          reading_level: rec.reading || '',
          writing_level: rec.writing || '',
          years_experience: null,
          last_used_date: null,
          verification_status: 'SELF_DECLARED',
          resume_visibility: 'OPTIONAL',
          details: {},
        });
      });

    // Standards
    standardRecords
      .filter((rec) => rec.standard)
      .forEach((rec) => {
        records.push({
          _backendId: extractBackendId(rec.id),
          capability_type: 'STANDARD',
          reference_value: null,
          custom_name: rec.standard || '',
          related_scope: null,
          edition_or_model: rec.edition || '',
          proficiency: PROFICIENCY_MAP[rec.proficiency] || '',
          speaking_level: '',
          reading_level: '',
          writing_level: '',
          years_experience: null,
          last_used_date: rec.last_used || null,
          verification_status: 'SELF_DECLARED',
          resume_visibility: 'OPTIONAL',
          details: {},
        });
      });

    // Equipment
    equipmentRecords
      .filter((rec) => rec.instrument_name)
      .forEach((rec) => {
        records.push({
          _backendId: extractBackendId(rec.id),
          capability_type: 'EQUIPMENT',
          reference_value: null,
          custom_name: rec.instrument_name || '',
          related_scope: null,
          edition_or_model: rec.make_model || '',
          proficiency: PROFICIENCY_MAP[rec.proficiency] || '',
          speaking_level: '',
          reading_level: '',
          writing_level: '',
          years_experience: null,
          last_used_date: rec.last_used || null,
          verification_status: 'SELF_DECLARED',
          resume_visibility: 'OPTIONAL',
          details: rec.calibration_authority
            ? { calibration_authority: rec.calibration_authority }
            : {},
        });
      });

    // Software
    softwareRecords
      .filter((rec) => rec.software_name)
      .forEach((rec) => {
        records.push({
          _backendId: extractBackendId(rec.id),
          capability_type: 'SOFTWARE',
          reference_value: null,
          custom_name: rec.software_name || '',
          related_scope: null,
          edition_or_model: '',
          proficiency: PROFICIENCY_MAP[rec.proficiency] || '',
          speaking_level: '',
          reading_level: '',
          writing_level: '',
          years_experience:
            rec.years_of_use === '' || rec.years_of_use == null
              ? null
              : Number(rec.years_of_use) || null,
          last_used_date: rec.last_used || null,
          verification_status: 'SELF_DECLARED',
          resume_visibility: 'OPTIONAL',
          details: {},
        });
      });

    return {
      professional_id: professionalProfileId,
      records,
    };
  };

  // ----- Submit -----
  const submit = async () => {
    if (!professionalProfileId) {
      throw new Error('Professional profile ID missing.');
    }
    const { records, professional_id } = buildPayload();
    const toCreate = records.filter((rec) => !rec._backendId);
    const toUpdate = records.filter((rec) => rec._backendId);

    if (toCreate.length > 0) {
      await axios.post(CAPABILITY_URL, {
        professional_id,
        records: toCreate.map(({ _backendId, ...rest }) => rest),
      });
    }
    if (toUpdate.length > 0) {
      await Promise.all(
        toUpdate.map(({ _backendId, ...rest }) =>
          axios.put(`${CAPABILITY_URL}${_backendId}/`, rest)
        )
      );
    }
  };

  // ----- Delete handler -----
  const removeCapabilityRecord = (setter, index, creator) => {
    const fieldMap = [
      { setter: setLanguageRecords, records: languageRecords },
      { setter: setStandardRecords, records: standardRecords },
      { setter: setEquipmentRecords, records: equipmentRecords },
      { setter: setSoftwareRecords, records: softwareRecords },
    ];
    const match = fieldMap.find((f) => f.setter === setter);
    const record = match?.records[index];
    const backendId = record ? extractBackendId(record.id) : null;

    // Remove from state
    setter((prev) => {
      if (prev.length === 1) return [creator()];
      return prev.filter((_, i) => i !== index);
    });

    // Delete from backend if exists
    if (backendId) {
      axios.delete(`${CAPABILITY_URL}${backendId}/`).catch((err) => {
        console.error('Failed to delete capability record', err);
        Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: 'Could not delete this record from the server.',
        });
      });
    }
  };

  // ----- Record management helpers -----
  const addRecord = (setter, creator) => {
    setter((prev) => [...prev, creator()]);
  };

  const removeRecord = (setter, index, creator) => {
    setter((prev) => {
      if (prev.length === 1) return [creator()];
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleRecordChange = (setter, index, field, value) => {
    setter((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleRecordFileChange = (setter, index, file, fileField = 'certificate_file') => {
    setter((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [fileField]: file,
        file_name: file ? file.name : '',
      };
      return updated;
    });
  };

  return {
    // State
    languageRecords,
    standardRecords,
    equipmentRecords,
    softwareRecords,
    // Setters
    setLanguageRecords,
    setStandardRecords,
    setEquipmentRecords,
    setSoftwareRecords,
    // Hydration
    hydrateFromCapabilities,
    hydrateFromRelatedRecords,
    // Validation (always true)
    validate: () => ({}),
    // Submit
    submit,
    // Delete handler
    removeCapabilityRecord,
    // Record helpers
    addRecord,
    removeRecord,
    handleRecordChange,
    handleRecordFileChange,
  };
};